import { forwardRef, useEffect, useRef, useState } from 'react';
// Import component CSS last
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { useIsRTL } from '../../../locale';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { Icon } from '../../Icon';
import cn from './SingleBreadcrumbDesign.css.json';
import './SingleBreadcrumbDesign.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SingleBreadcrumbDesign = /*#__PURE__*/forwardRef(function SingleBreadcrumbDesign(props, ref) {
  const dt = useDesignTokens();
  const isRTL = useIsRTL();
  const [isFocusOut, setIsFocusOut] = useState(true);
  const tokens = {
    ...getSingleBreadcrumbDesignTokens(dt, {
      tokens: props.tokens,
      variant: props.variant
    }),
    ...props.theme?.(dt)
  };
  const [timeoutId, setTimeoutId] = useState(null);
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);
  const renderRef = useRef(true);
  useEffect(() => {
    renderRef.current = false;
  }, []);
  const [exitAnimClass, setExitAnimClass] = useState('');
  useEffect(() => {
    if (props.animated && props.isLastItem && props.hideAnimation) {
      setExitAnimClass(cn.exitAnimation);
    } else {
      setExitAnimClass('');
    }
  }, [props.animated, props.isLastItem, props.hideAnimation]);
  const handleClick = e => {
    if (props.onPress) {
      e.preventDefault();
      const id = setTimeout(() => {
        props.onPress?.(props.index);
      }, 0);
      setTimeoutId(id);
    }
  };
  const refTruncation = useTruncationTooltip();
  const setKeyboardTooltipRef = useKeyboardTooltip({
    tooltip: props.icon?.tooltip ?? ''
  });
  const itemRef = element => {
    if (element) {
      setLinkRef(element);
      setKeyboardTooltipRef(element);
      if (props.focused && !isFocusOut) {
        element.focus();
      }
    }
  };

  // keyboardTooltip setLinkref
  const setLinkRef = element => {
    setRef(ref, element);
    setRef(refTruncation, element);
  };
  const commonProps = {
    className: cx(cn.itemText, props.isCurrent ? cn.current : cn.interactive, exitAnimClass),
    style: {
      ...tokens,
      ...props.style
    },
    tabIndex: props.focused ? 0 : -1,
    'aria-current': props.isCurrent ? props.ariaCurrent ?? 'page' : undefined,
    'aria-label': props.icon?.tooltip ? `${props.icon.tooltip}, ${props.value}` : `${props.value}`,
    onFocus: () => {
      props.onFocus?.();
      setIsFocusOut(false);
    },
    onKeyDown: event => props.onKeyDown?.(event),
    onBlur: () => {
      setIsFocusOut(true);
    },
    onClick: handleClick,
    'data-testid': props.testId
  };
  const duration = !props.hideAnimation ? 0 : 0.3;
  const position = isRTL ? '100%' : '-100%';
  const breadcrumbAnimation = {
    closed: {
      translate: position
    },
    open: {
      translate: 0,
      transition: {
        duration,
        ease: [0, 0.5, 0.2, 1]
      }
    }
  };
  return /*#__PURE__*/_jsx(AnimatePresence, {
    mode: "wait",
    children: /*#__PURE__*/_jsxs(motion.div, {
      variants: breadcrumbAnimation,
      animate: "open",
      initial: "closed",
      className: cx(cn.singleItem, props.focused && !isFocusOut && cn.singleItemFocus, exitAnimClass),
      style: {
        ...tokens,
        ...props.style
      },
      children: [props.icon && /*#__PURE__*/_jsx(Icon, {
        tooltip: "",
        ...props.icon
      }), props.isCurrent ? /*#__PURE__*/_jsx("span", {
        ...commonProps,
        ref: itemRef,
        role: "text",
        children: props.value
      }) : /*#__PURE__*/_jsx("a", {
        ref: itemRef,
        href: "#",
        ...commonProps,
        children: props.value
      })]
    }, "breadcrumb-item")
  });
});
export const getSingleBreadcrumbDesignTokens = (dt, opts) => {
  let textTokens = {
    fg: dt.tokens['color-action-text-control-normal'],
    focus: 'var(--focus-light-bg)',
    'font-family': 'inherit'
  };
  if (dt.themeVariant === 'light' && opts?.variant === 'primary-light') {
    textTokens = {
      ...textTokens,
      fg: dt.tokens['color-text-primary-inverse'],
      focus: 'var(--focus-dark-bg)'
    };
  }
  const tokenOverrides = opts?.tokens?.();
  if (tokenOverrides?.fg) {
    textTokens.fg = tokenOverrides.fg;
  }
  if (tokenOverrides?.fontFamily) {
    textTokens['font-family'] = tokenOverrides.fontFamily;
  }
  return {
    ...renderDesignTokens({
      ...textTokens,
      'font-weight-thick': dt.tokens['font-style-weight-700'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      'anim-tf-exit': dt.tokens['ease-out'],
      'anim-dur-enter': dt.tokens['duration-300'],
      'anim-tf-enter': dt.tokens['ease-in']
    }, 'breadcrumb-text'),
    ...(!dt.enableNovaTokens && getSingleBreadcrumbLegacyDesignTokens(dt, opts))
  };
};
const getSingleBreadcrumbLegacyDesignTokens = (dt, opts) => {
  let textTokens = {
    fg: dt.reportTokens.stateStandard.normal.fg,
    'font-family': 'inherit'
  };
  if (dt.themeVariant === 'light' && opts?.variant === 'primary-light') {
    textTokens = {
      fg: dt.c.white,
      'font-family': 'inherit'
    };
  }
  const tokenOverrides = opts?.tokens?.();
  if (tokenOverrides?.fg) {
    textTokens.fg = tokenOverrides.fg;
  }
  if (tokenOverrides?.fontFamily) {
    textTokens['font-family'] = tokenOverrides.fontFamily;
  }
  return {
    ...renderDesignTokens({
      ...textTokens,
      'font-weight-thick': dt.reportTokens.font.weight.thick,
      'font-size-m': dt.reportTokens.font.size.m
    }, 'breadcrumb-text')
  };
};
//# sourceMappingURL=SingleBreadcrumbDesign.js.map